/**
 * A ficha do ponto do "Próximos dias" — a previsão daquele lugar para os
 * próximos dias, como o meteograma do Weather Lab, do Windy e do meteoblue.
 * Mora no painel da previsão (`PainelDaPrevisao`: o cartão da direita no
 * desktop, a folha no celular), não num balão (`docs/proposta-previsao-entrada.md`).
 *
 * - **O número da ficha é o do mapa**: a mesma rodada e a mesma bilinear
 *   (`ponto.ts`); a área da célula (~28 km; ~14 km no ICON) em volta do ponto fica contornada no mapa.
 * - Linha de dias **no fuso do lugar** (o Acre está 2 h atrás de Brasília):
 *   chuva do dia e quando chove, máxima e mínima (das amostras de 3 h), e o
 *   chip da fumaça pela MÉDIA de 24 h do MP2,5, como a CONAMA define. Tocar
 *   num dia leva o mapa ao meio-dia dele.
 * - Janela de 1, 3, 7 ou 15 dias com ‹ › (Weather Lab); gráfico ou tabela.
 * - **Nada se mexe** sob o dedo ou o mouse, nem ao trocar de dia, de janela ou
 *   de forma: cartões de dia de altura fixa, leituras de largura fixa, a linha
 *   da hora lida sempre ocupada e a tabela na altura do gráfico.
 * - Com o **acesso de pesquisa** (WeatherNext 2, `acesso.ts`): a faixa da
 *   temperatura em que ficam 8 de cada 10 cenários, a chance de chuva no
 *   gráfico, na tabela e nos cartões largos do dia. O ECMWF continua sendo a
 *   linha e o número de cada hora.
 */
import clsx from 'clsx';
import { ChartLine, ChevronLeft, ChevronRight, CloudRain, Table2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DAY_MS, HOUR_MS } from '../lib/time';
import { useNow } from '../ui/hooks';
import { ATRIBUICAO_CAMS, ATRIBUICAO_WN2, SOBRE_MODELO_TEMPO, TERMOS_WN2_URL, instanteDaRodada, ladoDaCelulaKm } from './api';
import { camadaDe, corCss } from './camadas';
import { carregarPonto, lugarDoPonto, type DadosDoPonto, type Lugar } from './carregarPonto';
import { Barras, EixoDias, Faixa, Linhas, SetasVento, type Cursor } from './Meteograma';
import { atribuicaoDe, instantesDoModo } from './motor';
import { categoriaConama, horaLocal, inicioDoDiaLocal, resumoPorDia, rotuloDia, sensacaoTermica, type ParteDoDia, type ResumoDoDia } from './ponto';
import { tempoEmUso, usePrevisaoStore } from './store';
import { passoMaisProximo } from './tempo';

const JANELAS = [1, 3, 7, 15] as const;
type Janela = (typeof JANELAS)[number];

const COR_T = '#ff8f5a';
const COR_ST = '#ffc2a6';
const COR_PM = '#ffaa71';
const COR_VV = '#d4ec7a';
const COR_RAJ = '#ffd25a';
const COR_UR = '#6fb2e0';
const PALETA_CHUVA = camadaDe('chuva').paleta;
const PALETA_CHANCE = camadaDe('cenarios').paleta;

/** As faixas da CONAMA 491/2018 (MP2,5, 24 h) ao fundo da faixa da fumaça, com as cores do MonitorAr. */
const ZONAS_CONAMA = [
  { de: 0, ate: 25, cor: '#88CE40' },
  { de: 25, ate: 50, cor: '#FFD156' },
  { de: 50, ate: 75, cor: '#FFAA71' },
  { de: 75, ate: 125, cor: '#FF0000' },
  { de: 125, ate: 1000, cor: '#9B1BAF' },
];

const QUANDO: Record<ParteDoDia, string> = { madrugada: 'de madrugada', manhã: 'de manhã', tarde: 'à tarde', noite: 'à noite', 'o dia todo': 'o dia todo' };

const num = (v: number, casas = 0) => (Number.isFinite(v) ? v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }) : '—');
const mm = (v: number) => (v < 0.05 ? '0' : v < 10 ? num(v, 1) : num(v, 0));
const pequeno = 'text-[11px] pointer-coarse:text-xs';

function rotuloRodada(r: string): string {
  const d = new Date(instanteDaRodada(r));
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCHours())} UTC ${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}`;
}

/** O nome da faixa da CONAMA do valor horário (só para situar a linha nas zonas do fundo). */
const faixaConama = (v: number) => categoriaConama(v)?.nome.toLowerCase() ?? '';

/** "hoje" ou "dom 18" — o mesmo nome do dia nos cartões, no eixo e na tabela. */
function nomeDoDia(inicio: number, fuso: number, hoje: number): string {
  return inicio === hoje ? 'hoje' : rotuloDia(inicio, fuso);
}

/** Na ficha estreita (celular), a fumaça do dia é só a bolinha da cor (vazada: dia parcial); o nome fica no rótulo. */
function BolinhaAr({ dia, fimCams }: { dia: ResumoDoDia; fimCams: number | null }) {
  if (dia.ar) return <span className="size-2.5 shrink-0 rounded-full" style={{ background: dia.ar.cor }} aria-hidden />;
  if (fimCams !== null && dia.inicio < fimCams) return <span className="size-2.5 shrink-0 rounded-full ring-[1.5px] ring-ink-200 ring-inset" aria-hidden />;
  return null;
}

function ChipAr({ dia, fimCams }: { dia: ResumoDoDia; fimCams: number | null }) {
  if (!dia.ar) {
    // A categoria é da média de 24 h: o dia que não está inteiro (hoje, ou o último do CAMS) não tem.
    const dentroDoCams = fimCams !== null && dia.inicio < fimCams;
    return (
      <span
        className={clsx(pequeno, 'block w-full truncate leading-tight text-ink-400')}
        title={dentroDoCams ? 'O dia não está inteiro na previsão: a média de 24 h da fumaça não sai; veja a faixa da fumaça no gráfico.' : 'O CAMS vai até 5 dias.'}
      >
        {dentroDoCams ? 'ar: parcial' : 'ar: —'}
      </span>
    );
  }
  return (
    <span className={clsx(pequeno, 'flex w-full min-w-0 items-center gap-1 leading-tight text-ink-100')} title={`MP2,5 médio de 24 h: ${num(dia.mp25Media)} µg/m³ — ${dia.ar.saude}`}>
      <span className="size-2 shrink-0 rounded-full" style={{ background: dia.ar.cor }} aria-hidden />
      <span className="truncate">{dia.ar.nome}</span>
    </span>
  );
}

/**
 * Os dias do lugar, em cartões de tamanho FIXO. Na ficha larga (desktop), 7 de
 * uma vez, com cinco linhas sempre ocupadas; na estreita (celular), cartões de
 * três linhas (dia, máx./mín., chuva e a bolinha do ar) e a faixa sangra até a
 * borda da folha, esmaecendo à direita: o cartão cortado está claramente "a seguir".
 */
function LinhaDeDias(props: { dias: ResumoDoDia[]; janela: [number, number]; fimCams: number | null; aoEscolher: (d: ResumoDoDia) => void }) {
  return (
    <div
      className="-mx-(--borda) flex snap-x scroll-px-(--borda) gap-1.5 overflow-x-auto px-(--borda) pb-1 [mask-image:linear-gradient(to_right,#000_calc(100%-28px),transparent)] [scrollbar-width:none] @xl:[mask-image:none] @xl:[scrollbar-width:thin]"
      role="group"
      aria-label="Previsão por dia"
    >
      {props.dias.map((d) => {
        const naJanela = d.inicio + DAY_MS > props.janela[0] && d.inicio < props.janela[1];
        const nome = d.hoje ? 'hoje' : d.rotulo.split(' ')[0];
        const rotulo =
          `${d.hoje ? 'Hoje (só as horas que faltam)' : d.rotuloLongo}${d.parcial && !d.hoje ? ' (só parte do dia na previsão)' : ''}: ` +
          `máxima ${num(d.tMax)} graus, mínima ${num(d.tMin)}, chuva ${mm(d.chuva)} milímetros${d.quandoChove ? ` (${QUANDO[d.quandoChove]})` : ''}, ` +
          `${d.ar ? `ar ${d.ar.nome.toLowerCase()} pela fumaça (média de 24 horas)` : 'sem a média de 24 horas da fumaça'}` +
          `${Number.isFinite(d.chanceDia) ? `; chance de chuva de ${num(d.chanceDia)}% nos cenários do WeatherNext 2` : ''}${d.tendencia ? '; tendência, menos confiável' : ''}`;
        return (
          <button
            key={d.inicio}
            type="button"
            onClick={() => props.aoEscolher(d)}
            aria-label={rotulo}
            title={rotulo}
            data-dia-da-ficha=""
            className={clsx(
              // 7 na largura da ficha larga (@xl = 36 rem); estreita, ~4,3 à vista em 360 px.
              'relative flex h-[68px] w-[76px] shrink-0 snap-start flex-col items-start justify-between overflow-hidden rounded-lg border px-1.5 py-1 text-left transition-colors @xl:h-24 @xl:w-[calc((100%-36px)/7)]',
              'focus-visible:outline-2 focus-visible:outline-violet-400',
              d.tendencia ? 'border-dashed' : 'border-solid',
              naJanela ? 'border-violet-400/70 bg-violet-500/15' : 'border-ink-600 bg-ink-800/80 hover:bg-ink-700',
            )}
          >
            <span className="text-xs font-medium whitespace-nowrap text-ink-100">
              {nome} {d.rotulo.split(' ')[1]}
              {/* O "~" da tendência num lugar reservado: o rótulo tem a mesma largura com ou sem ele. */}
              <span className="inline-block w-[1ch]">{d.tendencia ? '~' : ''}</span>
            </span>
            <span className="text-sm whitespace-nowrap text-white tabular-nums">
              {num(d.tMax)}°<span className="text-ink-300">/{num(d.tMin)}°</span>
            </span>
            <span className={clsx(pequeno, 'flex w-full items-center gap-0.5 leading-tight whitespace-nowrap text-sky-200 tabular-nums')}>
              <CloudRain className="size-3 shrink-0" aria-hidden />
              {mm(d.chuva)} mm
            </span>
            {/* Estreita: a fumaça do dia no canto do cartão. */}
            <span className="absolute top-1.5 right-1.5 flex @xl:hidden">
              <BolinhaAr dia={d} fimCams={props.fimCams} />
            </span>
            <span className={clsx(pequeno, 'hidden w-full truncate leading-tight text-ink-300 @xl:block')}>
              {/* Com os cenários: a chance do dia (dos 64 cenários do WeatherNext 2) no lugar de quando chove, que não cabe junto. */}
              {Number.isFinite(d.chanceDia) ? `${num(d.chanceDia)}% chance` : (d.quandoChove ?? '\u00a0')}
            </span>
            <span className="hidden w-full @xl:flex">
              <ChipAr dia={d} fimCams={props.fimCams} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Tabela(props: { dados: DadosDoPonto; ks: number[]; fuso: number; hoje: number; altura: number | null }) {
  const { series: s } = props.dados;
  const linhas: [string, (k: number) => string][] = [
    ['Chuva (mm/h)', (k) => (Number.isFinite(s.chuva[k]) ? num(s.chuva[k]!, 1) : '—')],
    ['Temperatura (°C)', (k) => num(s.t2m[k]!)],
    ['Sensação (°C)', (k) => num(sensacaoTermica(s.t2m[k]!, s.ur[k]!))],
    ['MP2,5 (µg/m³)', (k) => num(s.pm25[k]!)],
    ['Vento (km/h)', (k) => num(s.vento[k]!)],
    ['Rajada (km/h)', (k) => num(s.rajada[k]!)],
    ['Umidade (%)', (k) => num(s.ur[k]!)],
  ];
  // O AIFS não tem rajada: a linha sai (em vez de uma fileira de "—").
  if (!props.dados.temRajada) linhas.splice(linhas.findIndex(([n]) => n.startsWith('Rajada')), 1);
  // Os cenários (acesso de pesquisa): logo abaixo da chuva e da temperatura do ECMWF.
  if (s.chance) linhas.splice(1, 0, ['Chance ≥ 1 mm em 6 h (%)', (k) => num(s.chance![k]!)]);
  if (s.t10 && s.t90) linhas.splice(s.chance ? 3 : 2, 0, ['8 em 10 cenários (°C)', (k) => (Number.isFinite(s.t10![k]!) ? `${num(s.t10![k]!)}–${num(s.t90![k]!)}` : '—')]);
  // A primeira coluna de cada dia ganha o nome dele e um separador (as horas não passam pela 0 h).
  const novoDia = (pos: number) => pos === 0 || inicioDoDiaLocal(s.t[props.ks[pos]!]!, props.fuso) !== inicioDoDiaLocal(s.t[props.ks[pos - 1]!]!, props.fuso);
  return (
    // No mínimo a altura do gráfico (medida): trocar Gráfico/Tabela não muda o tamanho da ficha. Só rola de lado
    // (para baixo rola o painel: rolagem dentro de rolagem prendia o dedo).
    <div className="-mx-1 overflow-x-auto overflow-y-hidden px-1 [scrollbar-width:thin]" style={props.altura ? { minHeight: props.altura } : undefined}>
      <table className={clsx(pequeno, 'border-separate border-spacing-0 tabular-nums')}>
        <caption className="sr-only">Previsão a cada 3 horas, no horário do lugar</caption>
        <thead>
          <tr>
            <th scope="col" className="sticky left-0 bg-ink-900 pr-2 text-left font-normal text-ink-300">
              hora
            </th>
            {props.ks.map((k, pos) => {
              const t = s.t[k]!;
              const dia = novoDia(pos);
              return (
                <th key={k} scope="col" className={clsx('px-1 text-right align-bottom font-normal whitespace-nowrap text-ink-300', dia && pos > 0 && 'border-l border-ink-600')}>
                  {dia && <span className="block text-left text-ink-100">{nomeDoDia(inicioDoDiaLocal(t, props.fuso), props.fuso, props.hoje)}</span>}
                  {String(horaLocal(t, props.fuso)).padStart(2, '0')}h
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {linhas.map(([nome, f]) => (
            <tr key={nome}>
              <th scope="row" className="sticky left-0 bg-ink-900 pr-2 text-left font-normal whitespace-nowrap text-ink-300">
                {nome}
              </th>
              {props.ks.map((k, pos) => (
                <td key={k} className={clsx('px-1 text-right text-ink-100', novoDia(pos) && pos > 0 && 'border-l border-ink-600')}>
                  {f(k)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** "24–31" (mín.–máx. da janela) para a escala de uma faixa; null sem valor. */
function minMax(serie: ArrayLike<number>, casas = 0): string | null {
  const vs = Array.from(serie).filter(Number.isFinite);
  if (!vs.length) return null;
  const lo = num(Math.min(...vs), casas);
  const hi = num(Math.max(...vs), casas);
  return lo === hi ? lo : `${lo}–${hi}`;
}

export function FichaDoPonto({ lon, lat }: { lon: number; lat: number }) {
  const indice = usePrevisaoStore((s) => s.indice);
  const tempo = usePrevisaoStore(tempoEmUso);
  const rIfs = usePrevisaoStore((s) => s.rodadas[tempoEmUso(s)]);
  const rCams = usePrevisaoStore((s) => s.rodadas.cams);
  const rWn2 = usePrevisaoStore((s) => s.rodadas.wn2);
  const instante = usePrevisaoStore((s) => s.instante);
  // O "hoje" acompanha a virada do dia com a ficha aberta.
  const agora = useNow();
  const [lugar, setLugar] = useState<Lugar | null>(null);
  const [dados, setDados] = useState<DadosDoPonto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [janela, setJanela] = useState<Janela>(3);
  const [pagina, setPagina] = useState(0);
  const [vista, setVista] = useState<'grafico' | 'tabela'>('grafico');
  const [sob, setSob] = useState<number | null>(null);
  const cursor: Cursor = { sob, aoSob: setSob };
  // A altura do gráfico, para a tabela ocupar o mesmo lugar.
  const graficoRef = useRef<HTMLDivElement>(null);
  const [alturaGrafico, setAlturaGrafico] = useState<number | null>(null);

  // Outro ponto: a ficha recomeça (sem os dados do anterior, na janela de 3 dias).
  useEffect(() => {
    setDados(null);
    setJanela(3);
    setPagina(0);
    setSob(null);
  }, [lon, lat]);

  useEffect(() => {
    let vivo = true;
    setLugar(null);
    void lugarDoPonto(lon, lat).then((l) => {
      if (!vivo) return;
      setLugar(l);
      // O nome vai para o cabeçalho do painel.
      usePrevisaoStore.setState({ lugarDoPonto: l });
    });
    return () => {
      vivo = false;
    };
  }, [lon, lat]);

  useEffect(() => {
    if (!indice) return;
    let vivo = true;
    setErro(null);
    // Outro modelo: nada dos números do anterior enquanto o novo carrega.
    setDados((d) => (d && d.tempo !== tempo ? null : d));
    carregarPonto(indice, { [tempo]: rIfs, cams: rCams, wn2: rWn2 }, lon, lat, Date.now(), tempo)
      .then((d) => vivo && setDados(d))
      .catch((e) => vivo && setErro(e instanceof Error ? e.message : String(e)));
    return () => {
      vivo = false;
    };
  }, [indice, tempo, rIfs, rCams, rWn2, lon, lat]);

  useLayoutEffect(() => {
    if (vista === 'grafico' && graficoRef.current) setAlturaGrafico(graficoRef.current.offsetHeight);
  }, [vista, janela, pagina, dados]);

  const fuso = lugar?.fuso ?? -3;
  const hora = Math.floor(agora / HOUR_MS);
  const hoje = inicioDoDiaLocal(hora * HOUR_MS, fuso);
  const dias = useMemo(() => (dados ? resumoPorDia(dados.series, fuso, hora * HOUR_MS, 7, dados.cenarios) : []), [dados, fuso, hora]);
  const t = dados?.series.t ?? [];
  const base = t.length ? inicioDoDiaLocal(t[0]!, fuso) : 0;
  const ultimaPagina = t.length ? Math.max(0, Math.ceil((t[t.length - 1]! - base) / (janela * DAY_MS)) - 1) : 0;
  const pag = Math.min(pagina, ultimaPagina);
  const ini = base + pag * janela * DAY_MS;
  const fim = ini + janela * DAY_MS;
  const ks = useMemo(() => t.map((x, k) => (x > ini && x <= fim ? k : -1)).filter((k) => k >= 0), [t, ini, fim]);
  const instJanela = ks.map((k) => t[k]!);
  const iAtual =
    instante === null
      ? -1
      : (() => {
          const i = passoMaisProximo(instJanela, instante);
          return i >= 0 && Math.abs(instJanela[i]! - instante) <= 1.5 * HOUR_MS ? i : -1;
        })();
  const lendo = sob ?? (iAtual >= 0 ? iAtual : null);
  const ler = (serie: ArrayLike<number> | undefined, casas = 0, unidade = '') => {
    if (lendo === null || !serie) return '—';
    const v = serie[ks[lendo]!]!;
    return Number.isFinite(v) ? `${num(v, casas)}${unidade}` : '—';
  };
  const corte = (serie: Float32Array) => Float32Array.from(ks, (k) => serie[k]!);
  const mudarJanela = (j: Janela) => {
    setJanela(j);
    setPagina(0);
    setSob(null);
  };
  const mudarPagina = (p: number) => {
    setPagina(p);
    setSob(null);
  };

  const escolherDia = (d: ResumoDoDia) => {
    const s = usePrevisaoStore.getState();
    const inst = instantesDoModo(s);
    // O meio-dia do LUGAR (hoje: o passo mais próximo de agora).
    const alvo = d.hoje ? Date.now() : d.inicio + 12 * HOUR_MS;
    const k = passoMaisProximo(inst, alvo);
    if (k >= 0) {
      s.setTocando(false);
      s.setInstante(inst[k]!);
    }
    mudarPagina(Math.max(0, Math.floor((d.inicio - base) / (janela * DAY_MS))));
  };

  if (erro || !dados) {
    return (
      <div data-ficha-previsao="" className="@container space-y-2 pt-2">
        <p className="text-xs text-ink-300">
          {!erro
            ? 'Carregando a previsão deste ponto…'
            : erro === 'ponto fora da área da previsão'
              ? 'Este ponto está fora da área da previsão (a Amazônia Legal).'
              : `Previsão indisponível agora (${erro}).`}
        </p>
      </div>
    );
  }

  const fimCams = dados.fimCams;
  const camsAcaba = fimCams !== null && instJanela.length > 0 && fimCams < instJanela[instJanela.length - 1]!;
  const s = dados.series;
  const sensacao = Float32Array.from(ks, (k) => sensacaoTermica(s.t2m[k]!, s.ur[k]!));
  const pm = corte(s.pm25);
  const pmMax = Math.max(75, ...Array.from(pm).filter(Number.isFinite));
  const tJanela = corte(s.t2m);
  const chuvaJanela = Array.from(ks, (k) => s.chuva[k]! * 3).filter(Number.isFinite).reduce((a, b) => a + b, 0);
  const resumo = `De ${nomeDoDia(inicioDoDiaLocal(instJanela[0] ?? ini, fuso), fuso, hoje)} a ${nomeDoDia(inicioDoDiaLocal(instJanela[instJanela.length - 1] ?? ini, fuso), fuso, hoje)}: temperatura de ${minMax(tJanela) ?? '—'} graus, chuva total de ${mm(chuvaJanela)} milímetros${minMax(pm) ? `, MP2,5 de ${minMax(pm)} microgramas por metro cúbico` : ''}.`;
  const horaLida = lendo !== null && instJanela[lendo] !== undefined ? `${nomeDoDia(inicioDoDiaLocal(instJanela[lendo]!, fuso), fuso, hoje)} · ${String(horaLocal(instJanela[lendo]!, fuso)).padStart(2, '0')}h` : '—';
  const sensacaoLida = lendo !== null ? sensacao[lendo]! : NaN;
  const rajadaLida = lendo !== null ? s.rajada[ks[lendo]!]! : NaN;
  const pmLido = lendo !== null ? pm[lendo]! : NaN;
  const t10 = s.t10 ? corte(s.t10) : null;
  const t90 = s.t90 ? corte(s.t90) : null;

  return (
    // O nome e as coordenadas estão no cabeçalho do painel; a área (~28 km; ~14 km no ICON) e a diferença de Brasília, no rodapé.
    <div data-ficha-previsao="" className="@container space-y-2">
      {/*
        Os dias e os controles ficam presos no topo ao rolar (só o gráfico ou a tabela anda) onde há altura: na ficha
        larga e na folha alta. Na folha média do celular rolariam numa fresta: lá rolam junto. 1 px acima: sem a
        fresta do arredondamento.
      */}
      <div data-topo-da-ficha="" className="relative -top-px z-10 @xl:sticky in-data-[encaixe=alta]:sticky -mx-(--borda) space-y-1 border-b border-ink-700/60 bg-ink-900 px-(--borda) pt-[5px] pb-1">
        <LinhaDeDias dias={dias} janela={[ini, fim]} fimCams={fimCams} aoEscolher={escolherDia} />
        {/* Uma linha: a janela (com ‹ › na ficha larga; na estreita, tocar num dia leva até ele) e a forma. */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              aria-label="Período anterior"
              title="Dias anteriores"
              disabled={pag === 0}
              onClick={() => mudarPagina(pag - 1)}
              className="hidden min-h-9 min-w-9 items-center justify-center rounded-md text-ink-200 hover:bg-ink-700 disabled:opacity-30 @xl:inline-flex"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </button>
            <div className="flex justify-center gap-0.5" role="group" aria-label="Janela do gráfico">
              {JANELAS.map((j) => (
                <button
                  key={j}
                  type="button"
                  aria-pressed={j === janela}
                  aria-label={j === 1 ? '1 dia' : `${j} dias`}
                  onClick={() => mudarJanela(j)}
                  className={clsx(
                    'min-h-9 min-w-10 rounded-md px-1.5 text-xs whitespace-nowrap pointer-coarse:min-h-11 pointer-coarse:min-w-11',
                    j === janela ? 'bg-violet-500/30 text-white' : 'text-ink-300 hover:bg-ink-700',
                  )}
                >
                  {j}d
                </button>
              ))}
            </div>
            <button
              type="button"
              aria-label="Próximo período"
              title="Dias seguintes"
              disabled={pag >= ultimaPagina}
              onClick={() => mudarPagina(pag + 1)}
              className="hidden min-h-9 min-w-9 items-center justify-center rounded-md text-ink-200 hover:bg-ink-700 disabled:opacity-30 @xl:inline-flex"
            >
              <ChevronRight className="size-4" aria-hidden />
            </button>
          </div>
          <div className="flex shrink-0 gap-0.5" role="group" aria-label="Forma">
            {(['grafico', 'tabela'] as const).map((v) => {
              const Icone = v === 'grafico' ? ChartLine : Table2;
              const nome = v === 'grafico' ? 'Gráfico' : 'Tabela';
              return (
                <button
                  key={v}
                  type="button"
                  aria-pressed={v === vista}
                  aria-label={nome}
                  title={nome}
                  onClick={() => setVista(v)}
                  className={clsx(
                    'inline-flex min-h-9 min-w-9 items-center justify-center gap-1 rounded-md px-2 text-xs pointer-coarse:min-h-11 pointer-coarse:min-w-11',
                    v === vista ? 'bg-ink-700 text-white' : 'text-ink-300 hover:bg-ink-700',
                  )}
                >
                  <Icone className="size-4 shrink-0" aria-hidden />
                  <span className="hidden @xl:inline">{nome}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <p className="sr-only">{resumo}</p>

      {vista === 'tabela' ? (
        <Tabela dados={dados} ks={ks} fuso={fuso} hoje={hoje} altura={alturaGrafico} />
      ) : (
        <div data-meteograma="" ref={graficoRef}>
          {/* A hora da leitura (do dedo ou, sem ele, a do mapa): uma vez só, no topo, sempre no mesmo lugar. */}
          <div className="flex h-6 items-center justify-between text-xs text-ink-200 tabular-nums">
            <span data-hora-lida="" className="w-[13ch] truncate">{horaLida}</span>
            <button
              type="button"
              onClick={() => setSob(null)}
              disabled={sob === null}
              className="h-6 text-[11px] text-ink-200 underline disabled:text-ink-400 disabled:no-underline"
              title="Voltar a leitura para a hora do mapa"
            >
              hora do mapa
            </button>
          </div>
          <Faixa titulo="Chuva" escala={`mm/h · ${minMax(corte(s.chuva), 1) ?? '—'}`} leitura={ler(s.chuva, 1, ' mm/h')}>
            <Barras serie={corte(s.chuva)} cor={(v) => corCss(PALETA_CHUVA, v)} instantes={instJanela} iAtual={iAtual} rotulo="Chuva prevista, em milímetros por hora" altura={30} cursor={cursor} />
          </Faixa>
          {s.chance && (
            // O limiar e o modelo primeiro: no celular a escala é cortada no fim.
            <Faixa titulo="Chance · WN2" escala="≥ 1 mm em 6 h · % dos 64 cenários" leitura={ler(s.chance, 0, '%')}>
              <Barras
                serie={corte(s.chance)}
                // Abaixo de 10% (sem cor no mapa), até 0%: um traço cinza baixo — "pouca chance", não "sem dado" (que fica vazio).
                cor={(v) => (v < 10 ? 'rgba(169,182,204,0.55)' : corCss(PALETA_CHANCE, v))}
                instantes={instJanela}
                iAtual={iAtual}
                rotulo="Chance de chuva (pelo menos 1 milímetro em 6 horas), em porcentagem dos 64 cenários do WeatherNext 2"
                altura={24}
                maximo={100}
                minimo={0}
                cursor={cursor}
              />
            </Faixa>
          )}
          <Faixa
            titulo="Temperatura"
            escala={`°C · ${minMax(tJanela) ?? '—'}${t10 ? ' · ▒ 8/10 cenários' : ''} · ┄ sensação`}
            leitura={
              <>
                {ler(s.t2m, 0, '°')}
                <span className="font-normal text-ink-300"> · sente {Number.isFinite(sensacaoLida) ? `${num(sensacaoLida)}°` : '—'}</span>
              </>
            }
          >
            <Linhas
              linhas={[
                { serie: tJanela, cor: COR_T },
                { serie: sensacao, cor: COR_ST, tracejada: true },
              ]}
              instantes={instJanela}
              iAtual={iAtual}
              rotulo={t10 ? 'Temperatura prevista e sensação térmica, com a faixa em que ficam 8 de cada 10 cenários do WeatherNext 2' : 'Temperatura prevista e sensação térmica'}
              altura={40}
              piso={6}
              banda={t10 && t90 ? { de: t10, ate: t90, cor: COR_T } : undefined}
              cursor={cursor}
            />
          </Faixa>
          <Faixa
            titulo={camsAcaba ? 'Fumaça (MP2,5) · o CAMS vai até 5 dias' : 'Fumaça (MP2,5)'}
            escala="µg/m³ · faixas da CONAMA"
            leitura={
              <>
                {ler(s.pm25, 0)}
                <span className="font-normal text-ink-300"> · {Number.isFinite(pmLido) ? faixaConama(pmLido) : '—'}</span>
              </>
            }
          >
            <Linhas
              linhas={[{ serie: pm, cor: COR_PM }]}
              instantes={instJanela}
              iAtual={iAtual}
              rotulo="MP2,5 previsto, com as faixas da CONAMA ao fundo"
              altura={32}
              fixo={[0, pmMax]}
              zonas={ZONAS_CONAMA}
              cursor={cursor}
            />
          </Faixa>
          <Faixa
            titulo={dados.temRajada ? 'Vento e rajada' : 'Vento'}
            escala={`km/h · ${minMax(corte(s.vento)) ?? '—'}${dados.temRajada ? '' : ` · sem rajada no ${SOBRE_MODELO_TEMPO[dados.tempo].curto}`}`}
            leitura={
              <>
                {ler(s.vento, 0, ' km/h')}
                {dados.temRajada && <span className="font-normal text-ink-300"> · raj. {Number.isFinite(rajadaLida) ? num(rajadaLida) : '—'}</span>}
              </>
            }
          >
            <Linhas
              linhas={[{ serie: corte(s.vento), cor: COR_VV }, ...(dados.temRajada ? [{ serie: corte(s.rajada), cor: COR_RAJ, pontos: true }] : [])]}
              instantes={instJanela}
              iAtual={iAtual}
              rotulo={dados.temRajada ? 'Vento e rajada previstos' : 'Vento previsto'}
              altura={28}
              piso={10}
              cursor={cursor}
            />
            <SetasVento rumo={corte(s.rumo)} />
          </Faixa>
          <Faixa titulo="Umidade" escala={`% · ${minMax(corte(s.ur)) ?? '—'}`} leitura={ler(s.ur, 0, '%')}>
            <Linhas linhas={[{ serie: corte(s.ur), cor: COR_UR }]} instantes={instJanela} iAtual={iAtual} rotulo="Umidade relativa prevista" altura={24} fixo={[0, 100]} cursor={cursor} />
          </Faixa>
          <EixoDias instantes={instJanela} fuso={fuso} hoje={hoje} />
        </div>
      )}
      {/* Rodapé de uma linha (pedido do usuário, 18/10/2026: o parágrafo de ressalvas ocupava a folha do celular).
          A explicação está em "Sobre a previsão", nos ajustes; o fuso, no cabeçalho. */}
      <p className={clsx(pequeno, 'truncate text-ink-400')} data-rodape-da-ficha="" title={`Modelo, não medição: cada valor é a média de uma área de ${ladoDaCelulaKm(dados.passo)}. Máx./mín. das amostras de 3 h${dados.tempo === 'aifs' ? ' (o AIFS tem passos de 6 h: as horas entre eles são interpoladas)' : ''}${SOBRE_MODELO_TEMPO[dados.tempo].rajada ? `; rajada: ${SOBRE_MODELO_TEMPO[dados.tempo].rajada}` : ''}; o ar do dia é a média de 24 h do MP2,5 (CONAMA nº 491/2018).`}>
        <span className="font-semibold text-violet-300">Modelo</span> ·{' '}
        <a
          className="underline decoration-ink-500 hover:text-ink-100"
          href={SOBRE_MODELO_TEMPO[dados.tempo].url}
          target="_blank"
          rel="noreferrer"
          title={atribuicaoDe(dados.tempo)}
          data-modelo-da-ficha={dados.tempo}
        >
          {SOBRE_MODELO_TEMPO[dados.tempo].nome}
          {SOBRE_MODELO_TEMPO[dados.tempo].ia && ' (IA)'}
        </a>{' '}
        {dados.rodadas[dados.tempo] && rotuloRodada(dados.rodadas[dados.tempo]!)}
        {dados.rodadas.cams && (
          <>
            {' · '}
            <a className="underline decoration-ink-500 hover:text-ink-100" href="https://atmosphere.copernicus.eu/" target="_blank" rel="noreferrer" title={ATRIBUICAO_CAMS}>
              CAMS
            </a>{' '}
            {rotuloRodada(dados.rodadas.cams)}
          </>
        )}
        {dados.rodadas.wn2 && (
          <>
            {' · '}
            <a
              className="underline decoration-ink-500 hover:text-ink-100"
              href={TERMOS_WN2_URL}
              target="_blank"
              rel="noreferrer"
              title={ATRIBUICAO_WN2}
            >
              WeatherNext 2
            </a>{' '}
            {rotuloRodada(dados.rodadas.wn2)} © Google
          </>
        )}
      </p>
    </div>
  );
}
